import PropTypes from 'prop-types';
import './Alert.css';

/**
 * Reusable Alert component for displaying notifications and messages
 *
 * @param {Object} props
 * @param {string} props.type - Alert type: 'success', 'error', 'warning', 'info'
 * @param {string} props.title - Optional alert title
 * @param {string} props.message - Alert message (can also use children)
 * @param {React.ReactNode} props.children - Alert content
 * @param {boolean} props.dismissible - Whether alert can be dismissed
 * @param {Function} props.onDismiss - Handler for dismiss action
 */
const Alert = ({
  type = 'info',
  title,
  message,
  children,
  dismissible = false,
  onDismiss,
  className = '',
}) => {
  const icons = {
    success: '✓',
    error: '✕',
    warning: '⚠',
    info: 'ℹ',
  };

  return (
    <div
      className={`alert alert--${type} ${className}`}
      role="alert"
      aria-live={type === 'error' ? 'assertive' : 'polite'}
    >
      <div className="alert-icon" aria-hidden="true">
        {icons[type]}
      </div>
      <div className="alert-content">
        {title && <div className="alert-title">{title}</div>}
        <div className="alert-message">{message || children}</div>
      </div>
      {dismissible && (
        <button
          type="button"
          className="alert-dismiss"
          onClick={onDismiss}
          aria-label="Dismiss alert"
        >
          ✕
        </button>
      )}
    </div>
  );
};

Alert.propTypes = {
  type: PropTypes.oneOf(['success', 'error', 'warning', 'info']),
  title: PropTypes.string,
  message: PropTypes.string,
  children: PropTypes.node,
  dismissible: PropTypes.bool,
  onDismiss: (props, propName, componentName) => {
    if (props.dismissible && !props[propName]) {
      return new Error(
        `\`${propName}\` is required when \`dismissible\` is true in \`${componentName}\`.`
      );
    }
  },
  className: PropTypes.string,
};

export default Alert;
